import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import { agreeDelivery, checkoutTotalDiscounted, getActiveStudent } from '@actions/checkout'
import { openModal, closeModal } from '@actions/modal'
import { updateActiveVoucher } from '@actions/activeVoucher.js'
import { listVariables, getOrdersByUser, isPixPaymentBlockByHourRange } from '@services/sm-api.js'
import { getDeliveryIntroText, getDeliveryCards, getDeliveryEstimateCard } from '../../../shared/deliveryModal'
import { educaLabelOf } from '@utils/product/ecosLabel'

import CreditCard from '@components/CreditCard'
import Billet from '@components/Billet'
import Pix from '@components/Pix'

import V2Layout from '../V2Layout'
import V2Stepper from '../V2Stepper'
import V2CartAside from '../V2CartAside'
import V2CoverStack from '../V2CoverStack'

// RESUMO DO PEDIDO (V2) — a etapa que centraliza os alertas do Grupo A
// (MAPA_ALERTAS_FLUXO_COMPRA.md). Regras preservadas (MATRIZ):
// - Entrega + aceite: P-10/12/13, PM-24 (texto B2B/B2C), PM-33 (gate), PM-34 (timestamp)
// - Disponibilidade dos meios: PM-07..PM-17 (boleto/cut dates), PM-13/14 (PIX por horário),
//   PM-10/11 (dois cartões), antecipadas como preview
// - Compra duplicada: PM-18/PM-19 (aviso com nº do pedido, decisão explícita)
// - Revisão de produtos por aluno (absorve C-01/C-04): "alterar produtos"
// - Totais: RC-02/03/07/08 (subtotal de/por, frete, total, economia)
// Transição: o CTA leva ao pagamento V1 (/steps/pagamento) até a tela de pagamento V2 existir.

const SERVICE_ID = 7
const DIGITAL_ID = 8

const money = v => Number(v || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
const cardBox = { marginTop: '20px', border: '1px solid #EFE7E3', borderRadius: '18px', padding: '20px', background: '#fff' }
const boxTitle = { margin: '0 0 16px', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '16px', color: '#373535' }

// PM-31/P-18: tipo de entrega Educamos derivado do sufixo da série
const educaDeliveryType = student => {
  if (!student || !student.serie_id) return null
  const suffix = String(student.serie_id).slice(1).toLowerCase()
  if (suffix.includes('em')) return student.ensino_em
  if (suffix.includes('f1')) return student.ensino_ef1
  if (suffix.includes('f2')) return student.ensino_ef2
  if (suffix.includes('ei') || suffix.includes('maternal')) return student.ensino_ei
  return null
}

const EDUCA_ID = 2
const thumbBox = { flex: 'none', width: '34px', height: '46px', borderRadius: '4px', background: '#FAF7F5', border: '1px solid #EFE7E3', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }

const ico = children => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
)
const METHOD_ICONS = {
  card: ico(<React.Fragment><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19M6.5 15h4" /></React.Fragment>),
  twocards: ico(<React.Fragment><rect x="2.5" y="7" width="13" height="10" rx="2" /><path d="M8.5 7V6a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-5" /></React.Fragment>),
  billet: ico(<path d="M4 5v14M7.5 5v14M11 5v14M13.5 5v14M17 5v14M20 5v14" />),
  pix: ico(<path d="M12 3.5l8.5 8.5-8.5 8.5L3.5 12z" />),
}

export default function V2Resumo() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const checkout = useSelector(state => state.checkout)
  const student = useSelector(state => getActiveStudent(state))
  const totalNow = useSelector(state => checkoutTotalDiscounted(state))
  const user = useSelector(state => (state.sessionReducer && state.sessionReducer.user) || {})
  const paymentOptions = useSelector(state => (state.school && state.school.payment_options) || {})
  const freight = useSelector(state => state.freight || {})

  const [accepted, setAccepted] = useState(false)
  const [variables, setVariables] = useState([])
  const [duplicated, setDuplicated] = useState(null)
  const [pixBlock, setPixBlock] = useState(null)
  const [method, setMethod] = useState(null)
  const [twoCardsRulesAccepted, setTwoCardsRulesAccepted] = useState(false)
  // pedido de 02/09: depois de tentar pagar sem o aceite, o bloco fica circulado em vermelho
  const [acceptAttention, setAcceptAttention] = useState(false)

  const _toggleAccept = () => {
    const next = !accepted
    setAccepted(next)
    if (next) setAcceptAttention(false)
    // PM-34: o aceite grava o timestamp de estimativa em todos os itens
    if (next) dispatch(agreeDelivery())
    if (!next) setMethod(null)
  }

  const isB2C = !!(student && student.school_is_b2c)
  const isImmediate = String(educaDeliveryType(student)) === '7' || student.immediate_delivery === 1

  // linhas de negócio físicas dos produtos ativos (PM-20)
  const activeProducts = checkout.flatMap(s => (s.products || []).filter(p => p.active))
  const physicalLines = [...new Set(activeProducts.map(p => p.business_line_id))].filter(
    id => id !== SERVICE_ID && id !== DIGITAL_ID
  )
  const onlyDigitalOrService = physicalLines.length === 0 && activeProducts.length > 0 // PM-21

  // PM-28/29: estimativa por cut date
  const cutDates = (student && student.contract_cut_dates) || []
  const futureCut = cutDates.find(d => moment(d).isAfter(moment()))
  const lastCut = cutDates.length ? cutDates[cutDates.length - 1] : null
  const cutDateActive = lastCut ? moment().isBefore(moment(lastCut)) : false
  const deliveryEstimateValue = futureCut ? moment(futureCut).add(20, 'days').format('DD/MM/YYYY') : null

  // PM-15/16: indisponibilidade do boleto por cut date / fim de contrato
  const today = moment()
  const inSpecialPeriod = today.isBetween(moment(`${today.year()}-01-01`), moment(`${today.year()}-03-16`), 'day', '[]')
  const nearCutDate = cutDates.some(d => {
    const cd = moment(d)
    return today.isSame(cd, 'day') || (today.isAfter(cd.clone().subtract(4, 'days')) && today.isBefore(cd))
  })
  const contractEnd = student && student.contract_end ? moment(student.contract_end, ['DD/MM/YYYY', 'YYYY-MM-DD']) : null
  const nearContractEnd =
    contractEnd && (today.isSame(contractEnd, 'day') || (today.isAfter(contractEnd.clone().subtract(4, 'days')) && today.isBefore(contractEnd)))
  const billetBlocked = (inSpecialPeriod && nearCutDate) || !!nearContractEnd

  useEffect(() => {
    listVariables().then(vars => setVariables(Array.isArray(vars) ? vars : [])).catch(() => {})
    isPixPaymentBlockByHourRange().then(r => setPixBlock(r)).catch(() => {})
    // PM-18: verificação de compra duplicada, antecipada para o Resumo
    if (user && user.id && student && student.voucher) {
      getOrdersByUser({ id: user.id })
        .then(resp => {
          const orders = (resp && resp.orders) || resp || []
          const found = (Array.isArray(orders) ? orders : []).find(
            o =>
              ['PG', 'AP', 'BAP', 'PAV'].includes(o.payment_status) &&
              JSON.stringify(o).toUpperCase().includes(String(student.voucher).toUpperCase())
          )
          if (found) setDuplicated(found.order_id)
        })
        .catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getVar = key => {
    const v = variables.find(x => x.key === key)
    return v ? v.value : null
  }
  const hybridBillet = getVar('HYBRID_BILLET_ACTIVATION') === '1'
  const pixEnabled = getVar('PIX_ITAU_ACTIVATION') === '1'
  const pixBlocked = pixBlock && pixBlock.status === 'blocked'

  // PM-10/11: dois cartões
  const firstStudent = checkout[0]
  const hasEduca = firstStudent && (firstStudent.products || []).some(p => p.business_line_id === 2 && p.active)
  const twoCardsAvailable = !!paymentOptions.has_two_credit_cards && hasEduca
  const twoCardsUsable = twoCardsAvailable && checkout.length === 1

  // Pendências (alertas centralizados)
  const alerts = []
  if (duplicated)
    alerts.push(`Já existe um pedido (${duplicated}) com este voucher para o seu CPF. Continue apenas se quiser realizar outra compra.`)
  if (billetBlocked)
    alerts.push('Pagamento por boleto indisponível: o lote de envio será encerrado em alguns dias e o boleto pode levar até 3 dias para compensar.')
  if (pixEnabled && pixBlocked && pixBlock.hour_range && pixBlock.hour_range.length === 2)
    alerts.push(`PIX temporariamente indisponível entre ${pixBlock.hour_range[0]}h e ${pixBlock.hour_range[1]}h.`)
  if (twoCardsAvailable && !twoCardsUsable)
    alerts.push('Pagamento em dois cartões exige apenas um aluno no carrinho. Para usá-lo, faça compras separadas.')


  const paymentUnlocked = accepted || onlyDigitalOrService

  // Feedback ativo do gate PM-33: clicar num meio ainda bloqueado explica o motivo
  // e leva o usuário até o aceite (em vez de um botão desabilitado sem explicação)
  const _showLockedNotice = () => {
    setAcceptAttention(true)
    const _close = () => {
      dispatch(closeModal())
      const accept = document.getElementById('v2-resumo-accept')
      if (accept) accept.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    dispatch(
      openModal(
        <div onClick={_close} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box' }}>
          <div
            id="v2-locked-payment-modal"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '420px', width: '100%', background: '#fff', borderRadius: '18px', padding: '28px', textAlign: 'center', fontFamily: 'Lato, Helvetica, Arial, sans-serif', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
          >
            <p style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '18px', color: '#373535' }}>Falta um passo</p>
            <p style={{ margin: '12px 0 0', fontSize: '15px', lineHeight: '1.6', color: '#6B6664' }}>
              Aceite a estimativa de entrega, logo acima das formas de pagamento, para liberá-las.
            </p>
            <button
              id="v2-locked-payment-ok"
              type="button"
              onClick={_close}
              style={{ marginTop: '20px', width: '100%', height: '48px', border: 'none', borderRadius: '12px', background: '#D8262C', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', cursor: 'pointer' }}
            >
              Ir para o aceite
            </button>
          </div>
        </div>
      )
    )
  }

  // resumo leve para a tela de Conclusão (a resposta da API não traz totais/aluno
  // e o carrinho é limpo no sucesso — D-13); nenhum dado sensível, só rótulos
  useEffect(() => {
    // não sobrescrever quando o carrinho esvazia (sucesso do pagamento limpa o checkout
    // ANTES da navegação — o resumo gravado precisa sobreviver para a tela de Conclusão)
    if (!checkout.length) return
    const first = checkout[0] || {}
    sessionStorage.setItem(
      'smd_v2_order_summary',
      JSON.stringify({
        name: first.name,
        voucher: first.voucher,
        students: checkout.length,
        total: totalNow + (isB2C ? Number(freight.calculated_freight || 0) : 0),
        // pedido de negócio (09/09, itens 9/10): todos os alunos, pagamento e entrega
        list: checkout.map(st => ({
          name: st.name,
          voucher: st.voucher,
          items: (st.products || []).filter(p => p.active).length,
          total: st.total_discounted,
        })),
        isB2C,
        estimate: isImmediate
          ? 'Em 48 horas ou de acordo com orientação da escola'
          : !isB2C && cutDateActive && deliveryEstimateValue
            ? deliveryEstimateValue
            : 'Em até 20 dias úteis após a confirmação do pagamento',
        method,
      })
    )
  }, [checkout, totalNow, isB2C, freight, isImmediate, cutDateActive, deliveryEstimateValue, method])

  const methods = [
    {
      key: 'billet',
      name: hybridBillet ? 'Boleto/PIX' : 'Boleto',
      available: paymentOptions.has_billet === 1 && !billetBlocked,
      note: billetBlocked ? 'Indisponível: lote de envio próximo do encerramento' : 'Compensação em até 3 dias úteis',
    },
    {
      key: 'card',
      name: 'Cartão de crédito',
      available: paymentOptions.has_credit_card === 1,
      note: 'Parcelamento conforme condições da escola',
    },
    ...(pixEnabled
      ? [{ key: 'pix', name: 'PIX', available: !pixBlocked, note: pixBlocked ? 'Indisponível na janela de horário atual' : 'Aprovação em minutos, chave expira em 10 min' }]
      : []),
    ...(twoCardsAvailable
      ? [{ key: 'twocards', name: 'Dois cartões', available: twoCardsUsable, note: twoCardsUsable ? 'Divida o kit Educamos entre dois cartões' : 'Disponível apenas com um aluno no carrinho' }]
      : []),
  ]

  return (
    <V2Layout>
      <V2Stepper current={3} />
      <div className="v2-step-grid" style={{ maxWidth: '1200px', margin: '0 auto', padding: '56px 32px 110px', display: 'grid', gridTemplateColumns: '1.55fr 0.95fr', gap: '40px', alignItems: 'start' }}>
        {/* Coluna principal */}
        <div>
          <span className="v2-eyebrow">Etapa 4 de 5</span>
          <h1 className="v2-step-title">Confira e escolha como pagar</h1>
          <p style={{ margin: '12px 0 0', maxWidth: '620px', fontSize: '16px', lineHeight: '1.6', color: '#6B6664' }}>
            Confira os dados, escolha a forma de pagamento e finalize. Nada é cobrado antes de você confirmar.
          </p>
          <p style={{ margin: '8px 0 0', fontSize: '14px' }}>
            <button
              type="button"
              onClick={() => {
                sessionStorage.setItem('smd_v2_flow', '1')
                navigate('/v2/steps/atualizacao')
              }}
              style={{ border: 'none', background: 'none', padding: 0, fontSize: '14px', fontWeight: '700', color: '#D8262C', cursor: 'pointer' }}
            >
              Atualizar dados cadastrais →
            </button>
          </p>

          {/* Produtos por aluno (absorve a etapa de Confirmação C-01/C-04) */}
          {checkout.map((s, idx) => (
            <div key={`${s.voucher}-${idx}`} style={{ marginTop: '24px', border: '1px solid #EFE7E3', borderRadius: '18px', overflow: 'hidden', background: '#fff' }}>
              <div style={{ padding: '18px 20px', borderBottom: '1px solid #EFE7E3', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: '#FAF7F5' }}>
                <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '16px', color: '#373535' }}>Produtos · {s.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    dispatch(updateActiveVoucher(s.voucher, s.name))
                    navigate('/v2/steps/selecionar-livros')
                  }}
                  style={{ border: 'none', background: 'none', padding: 0, fontSize: '13px', fontWeight: '700', color: '#D8262C', cursor: 'pointer' }}
                >
                  alterar produtos
                </button>
              </div>
              {/* pedido de negócio (09/09, item 6): miniaturas como na seleção e o kit
                  Educamos como uma linha só (capas em leque + total), em vez de N itens */}
              {(() => {
                const active = (s.products || []).filter(p => p.active)
                const educa = active.filter(p => p.business_line_id === EDUCA_ID)
                const others = active.filter(p => p.business_line_id !== EDUCA_ID)
                const educaTotal = educa.reduce((t, p) => t + Number(p.amount_discounted || 0), 0)
                return (
                  <React.Fragment>
                    {others.map(p => {
                      const cover = p.images && p.images.GraficoBig ? p.images.GraficoBig : null
                      return (
                        <div key={p.product_sap_code} className="v2-resumo-item" style={{ padding: '10px 20px', borderBottom: '1px solid #F4ECE9', display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <span style={thumbBox}>{cover ? <img src={cover} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : null}</span>
                          <span style={{ flex: 1, minWidth: 0, fontSize: '14px', lineHeight: '1.4', color: '#373535' }}>{p.name}</span>
                          <span style={{ flex: 'none', fontSize: '14px', fontWeight: '700', color: '#373535' }}>{money(p.amount_discounted)}</span>
                        </div>
                      )
                    })}
                    {educa.length > 0 && (
                      <div className="v2-resumo-item v2-resumo-item--pack" style={{ padding: '12px 20px', borderBottom: '1px solid #F4ECE9', display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{ flex: 'none', width: '118px' }}>
                          <V2CoverStack items={educa} size={46} max={5} overlap={0.6} align="flex-start" badge={false} />
                        </div>
                        <span style={{ flex: 1, minWidth: 0, fontSize: '14px', lineHeight: '1.4', color: '#373535' }}>
                          <strong style={{ display: 'block', fontFamily: 'Poppins,sans-serif', fontWeight: '600' }}>
                            {educaLabelOf(educa)} — {s.study_year} · Livro do Aluno
                          </strong>
                          <span style={{ fontSize: '12.5px', color: '#6B6664' }}>Sistema de Ensino · {educa.length} itens</span>
                        </span>
                        <span style={{ flex: 'none', fontSize: '14px', fontWeight: '700', color: '#373535' }}>{money(educaTotal)}</span>
                      </div>
                    )}
                  </React.Fragment>
                )
              })()}
            </div>
          ))}

          {/* Entrega + aceite (centraliza o modal PM-22..33 e o alerta P-08) */}
          {!onlyDigitalOrService && (
            <div style={cardBox} id="v2-resumo-entrega">
              <p style={boxTitle}>Entrega</p>
              <div style={{ fontSize: '14px', color: '#6B6664' }}>
                {getDeliveryIntroText(isImmediate, isB2C, activeProducts.filter(p => ![SERVICE_ID, DIGITAL_ID].includes(p.business_line_id)).length === 1)}
              </div>
              <div className="v2-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', margin: '14px 0 0' }}>
                {getDeliveryCards(physicalLines, isImmediate, isB2C, educaDeliveryType(student)).map(card => (
                  <div key={card.key} style={{ border: '1px solid #EFE7E3', borderRadius: '12px', padding: '12px 14px' }}>
                    <strong style={{ display: 'block', fontSize: '14px', color: '#373535' }}>{card.title}</strong>
                    {card.subtitle && <span style={{ fontSize: '12px', color: '#6B6664' }}>{card.subtitle}</span>}
                  </div>
                ))}
              </div>
              {/* a estimativa aparece UMA vez, no bloco do aceite (pedido de negócio 09/09, item 7);
                  o aceite (PM-33/34) fica no aside, junto das formas de pagamento que ele libera */}
            </div>
          )}


        </div>

        <V2CartAside
          primary={false}
          ctaOutside
          cta={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Aceite da estimativa de entrega (PM-33 gate, PM-34 timestamp) — junto das
              formas de pagamento que ele libera (pedido de 02/09): a pendência é o
              próprio checkbox, e o bloco fica verde quando aceito */}
          {!onlyDigitalOrService && (
            <div
              id="v2-resumo-accept-box"
              className={acceptAttention && !accepted ? 'v2-accept-attention' : ''}
              style={{
                border: acceptAttention && !accepted ? '2px solid #D8262C' : `1px solid ${accepted ? '#CFE5D8' : '#F1DCB4'}`,
                borderLeft: `4px solid ${accepted ? '#16603C' : acceptAttention ? '#D8262C' : '#e0a52a'}`,
                borderRadius: '12px',
                background: accepted ? '#E8F4EE' : acceptAttention ? '#FDEAEA' : '#FFF4E2',
                boxShadow: acceptAttention && !accepted ? '0 0 0 4px rgba(216, 38, 44,0.16)' : 'none',
                padding: '14px 18px',
                transition: 'border-color .2s, box-shadow .2s, background .2s',
              }}
            >
              <p style={{ margin: '0 0 6px', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '14px', color: accepted ? '#16603C' : acceptAttention ? '#C2262B' : '#8A5A10' }}>
                {accepted
                  ? 'Estimativa de entrega aceita'
                  : acceptAttention
                    ? 'Falta este aceite para liberar o pagamento'
                    : 'Aceite a estimativa de entrega para liberar o pagamento'}
              </p>
              <div className="v2-resumo-estimate" style={{ fontSize: '13px', lineHeight: '1.5', color: '#373535' }}>
                {getDeliveryEstimateCard(isImmediate, isB2C, cutDateActive, deliveryEstimateValue)}
              </div>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '10px', cursor: 'pointer' }}>
                <input id="v2-resumo-accept" type="checkbox" checked={accepted} onChange={_toggleAccept} style={{ marginTop: '3px' }} />
                <span style={{ fontSize: '13.5px', lineHeight: '1.45', color: '#373535' }}>
                  {isB2C
                    ? 'Estou ciente da estimativa de entrega.'
                    : 'Estou ciente da estimativa de entrega na escola.'}
                </span>
              </label>
            </div>
          )}
          {/* Demais pendências (boleto/PIX/dois cartões/compra duplicada) — acima da
              forma de pagamento, junto de onde o usuário tenta agir (pedido de 30/08) */}
          {alerts.length > 0 && (
            <div id="v2-resumo-alerts" style={{ border: '1px solid #F1DCB4', borderLeft: '4px solid #e0a52a', borderRadius: '12px', background: '#FFF4E2', padding: '16px 18px' }}>
              <p style={{ margin: '0 0 8px', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '14px', color: '#8A5A10' }}>
                Pendências antes de finalizar
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {alerts.map((a, i) => (
                  <span key={i} style={{ fontSize: '13px', lineHeight: '1.5', color: '#8A5A10' }}>• {a}</span>
                ))}
              </div>
            </div>
          )}
          {/* Formas de pagamento — seleção e finalização inline (design isCard).
              Os componentes V1 (CreditCard/Billet/Pix) são reusados por inteiro:
              CC-01..CC-25, B-01..B-09, PX-01..PX-17 preservadas, incluindo a
              rechecagem de duplicidade no Finalizar (CC-12). */}
          <div className="v2-pay" id="v2-resumo-payment">
            <p style={boxTitle}>Como você quer pagar?</p>
            <div className="v2-pay__grid">
              {methods.map(m => {
                const selectable = m.available && paymentUnlocked
                const selected = method === m.key
                return (
                  <button
                    key={m.key}
                    id={`v2-method-${m.key}`}
                    type="button"
                    disabled={!m.available}
                    onClick={() => {
                      // PM-33: método disponível mas aceite pendente → explica em pop-up
                      if (!paymentUnlocked) {
                        _showLockedNotice()
                        return
                      }
                      setMethod(selected ? null : m.key)
                    }}
                    style={{ textAlign: 'left', cursor: m.available ? 'pointer' : 'not-allowed', border: `1.5px solid ${selected ? '#D8262C' : m.available ? '#E3DBD7' : '#f0e3e2'}`, background: selected ? '#FDEAEA' : m.available ? '#fff' : '#faf7f7', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '4px', opacity: selectable ? 1 : 0.7 }}
                  >
                    <span className="v2-pay__icon" style={{ color: selected ? '#D8262C' : '#6B6664' }}>{METHOD_ICONS[m.key]}</span>
                    <span style={{ fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', color: m.available ? '#373535' : '#6B6664' }}>{m.name}</span>
                    <span style={{ fontSize: '12px', lineHeight: '1.4', color: '#6B6664' }}>{m.note}</span>
                  </button>
                )
              })}
            </div>

            {method === 'card' && (
              <div className="v2-uuf" style={{ marginTop: '18px' }}>
                <CreditCard />
              </div>
            )}
            {method === 'billet' && (
              <div className="v2-uuf" style={{ marginTop: '18px' }}>
                <Billet hybridBilletActivation={hybridBillet ? '1' : '0'} />
              </div>
            )}
            {method === 'pix' && (
              <div className="v2-uuf" style={{ marginTop: '18px' }}>
                <Pix />
              </div>
            )}
            {method === 'twocards' && (
              /* TC-02: painel de regras com aceite obrigatório (textos literais do V1);
                 o Continuar leva à segmentação V2 (TC-04..TC-13 na rota dedicada) */
              <div id="v2-twocards-rules" style={{ marginTop: '18px', padding: '16px', border: '1px solid #EFE7E3', borderRadius: '12px', fontSize: '13.5px', lineHeight: '1.55', color: '#6B6664' }}>
                <p style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '14px', color: '#373535' }}>
                  Regras de pagamento com 2 cartões
                </p>
                <p style={{ margin: '10px 0 0' }}>
                  - Para realizar o pagamento, será necessário selecionar os ítens que deverão ser pagos em cada cartão.
                </p>
                <p style={{ margin: '8px 0 0' }}>
                  - O pagamento será confirmado após a validação de ambos os cartões. Caso algum deles não seja
                  confirmado, o pedido será automaticamente cancelado e será feito o estorno do cartão confirmado
                </p>
                <p style={{ margin: '8px 0 0' }}>- A logística de entrega não será afetada em função dessa forma de pagamento.</p>
                <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '12px', cursor: 'pointer' }}>
                  <input
                    id="v2-twocards-accept"
                    type="checkbox"
                    checked={twoCardsRulesAccepted}
                    onChange={() => setTwoCardsRulesAccepted(!twoCardsRulesAccepted)}
                    style={{ marginTop: '3px' }}
                  />
                  <span style={{ color: '#373535' }}>Estou ciente das regras e desejo continuar</span>
                </label>
                <button
                  id="v2-twocards-continue"
                  type="button"
                  disabled={!twoCardsRulesAccepted}
                  onClick={() => {
                    sessionStorage.setItem('smd_v2_twocards', '1')
                    navigate('/v2/steps/pagamento-dois-cartoes')
                  }}
                  style={{ marginTop: '14px', width: '100%', height: '46px', border: 'none', borderRadius: '12px', background: twoCardsRulesAccepted ? '#D8262C' : '#f0b5b1', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '14px', cursor: twoCardsRulesAccepted ? 'pointer' : 'not-allowed' }}
                >
                  Continuar
                </button>
              </div>
            )}
          </div>
          <div className="v2-after">
            <strong>O que acontece depois</strong>
            <p>
              {onlyDigitalOrService
                ? 'Confirmado o pagamento, a licença é enviada para o e-mail da compra. Você acompanha o pedido em Minha conta.'
                : isB2C
                  ? 'Confirmado o pagamento, o pedido é preparado e enviado para o endereço informado. O rastreio aparece em Minha conta quando sair com a transportadora.'
                  : 'Confirmado o pagamento, o material segue para a escola, que informa a data de entrega. Você acompanha o pedido em Minha conta.'}
            </p>
          </div>
              <button
                type="button"
                onClick={() => navigate('/v2/steps/selecionar-livros')}
                style={{ border: 'none', background: 'none', padding: '4px', fontSize: '13px', fontWeight: '700', color: '#6B6664', cursor: 'pointer' }}
              >
                ← Voltar
              </button>
            </div>
          }
        />
      </div>
    </V2Layout>
  )
}
